import React, { useState, DragEvent } from "react";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import { MasterPlanTask } from "../../services/masterPlan";
import TaskModal from "../TaskModal";
import TaskDetailPanel from "../TaskDetailPanel";
import { formatTaskStatus } from "../../utils/enumFormatting";
import { usePersistedPreference } from "../../hooks/useFilterPersistence";

interface TaskKanbanViewProps {
  tasks: MasterPlanTask[];
  entityType?: "company" | "segment" | "proposition" | "position" | "okr";
  entityId?: string;
  canEdit: boolean;
  organizationId: string;
  positions?: any[];
  segments?: any[];
  propositions?: any[];
  groups?: any[];
  companies?: any[];
  onTaskUpdate: (taskId: string, updates: Partial<MasterPlanTask>) => void;
  onTaskCreate: (task: MasterPlanTask) => void;
  onTaskDelete: (taskId: string) => void;
  hideCreateButton?: boolean;
  preferenceNamespace?: string;
}

const TaskKanbanView: React.FC<TaskKanbanViewProps> = ({
  tasks,
  entityType,
  entityId,
  canEdit,
  organizationId,
  positions = [],
  segments = [],
  propositions = [],
  groups = [],
  companies = [],
  onTaskUpdate,
  onTaskCreate,
  onTaskDelete,
  hideCreateButton = false,
  preferenceNamespace,
}) => {
  const [showAddTask, setShowAddTask] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [selectedTask, setSelectedTask] = useState<MasterPlanTask | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);

  const initialBlockedVisibility =
    typeof window !== "undefined" ? window.innerWidth >= 1440 : true;
  const preferenceScope = preferenceNamespace
    ? `taskKanban.${preferenceNamespace}`
    : entityType
      ? `taskKanban.${entityType}`
      : "taskKanban.default";
  const [showBlockedPanel, setShowBlockedPanel] =
    usePersistedPreference<boolean>(
      "blockedPanelVisible",
      initialBlockedVisibility,
      preferenceScope,
    );

  // Status columns for Kanban
  const statuses = ["Not Started", "In Progress", "Complete", "Blocked"];

  // Backend now handles entity filtering, so we just use tasks directly
  const filteredTasks = tasks;

  // Group tasks by status
  const tasksByStatus = React.useMemo(() => {
    const grouped: Record<string, MasterPlanTask[]> = {
      "Not Started": [],
      "In Progress": [],
      Complete: [],
      Blocked: [],
    };

    filteredTasks.forEach((task) => {
      // Format the status from snake_case to Title Case for grouping
      const formattedStatus = formatTaskStatus(task.status);
      if (grouped[formattedStatus]) {
        grouped[formattedStatus].push(task);
      }
    });

    return grouped;
  }, [filteredTasks]);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, status: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStatus(status);
  };

  const handleDragLeave = () => {
    setDragOverStatus(null);
  };

  const handleDrop = async (
    e: DragEvent<HTMLDivElement>,
    newStatus: string,
  ) => {
    e.preventDefault();
    setDragOverStatus(null);

    if (draggedTaskId && canEdit) {
      // Convert Title Case back to snake_case for the API
      const apiStatus = newStatus.toLowerCase().replace(/ /g, "_") as
        | "not_started"
        | "in_progress"
        | "complete"
        | "blocked";

      await onTaskUpdate(draggedTaskId, {
        status: apiStatus,
      });
    }

    setDraggedTaskId(null);
  };

  const handleViewTask = (task: MasterPlanTask) => {
    setSelectedTask(task);
    setShowDetailPanel(true);
  };

  const handleEditTask = (task: MasterPlanTask, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    setSelectedTask(task);
    setShowDetailPanel(true);
  };

  const getAssigneeName = (task: MasterPlanTask) => {
    return task.assigned_to_name || "Unassigned";
  };

  const getContextDisplay = (task: MasterPlanTask) => {
    // Check both context_object_type and context_type for backward compatibility
    const contextType = task.context_object_type || task.context_type;
    if (!contextType || !task.context_object_id) return null;

    switch (contextType) {
      case "position": {
        const position = positions?.find(
          (p) => p.id === task.context_object_id,
        );
        return position
          ? `${position.proposition_name} × ${position.segment_name}`
          : "";
      }
      case "segment": {
        const segment = segments?.find((s) => s.id === task.context_object_id);
        return segment?.name || "";
      }
      case "proposition": {
        const proposition = propositions?.find(
          (p) => p.id === task.context_object_id,
        );
        return proposition?.name || "";
      }
      case "group": {
        const group = groups?.find((g) => g.id === task.context_object_id);
        return group?.name || "";
      }
      case "company": {
        const company = companies?.find((c) => c.id === task.context_object_id);
        return company?.name || "";
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header - Only show if not hiding create button (which indicates it's in MyTasks) */}
      {!hideCreateButton && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-4 py-3 sm:px-6 sm:py-4">
          <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
            Tasks - Kanban View
            {entityType && (
              <span className="ml-2 text-xs text-gray-500 sm:text-sm">
                ({filteredTasks.length} tasks)
              </span>
            )}
          </h2>
          {canEdit && (
            <button
              onClick={() => setShowAddTask(true)}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add Task
            </button>
          )}
        </div>
      )}

      {/* Scrollable Kanban Board */}
      <div className="flex-1 overflow-hidden px-2 py-4 sm:px-4 lg:px-6">
        <div className="h-full overflow-x-auto overflow-y-auto">
          <div className="inline-flex h-full min-w-full gap-3 pb-4 sm:gap-4 md:grid md:grid-cols-2 xl:grid-cols-3">
            {statuses
              .filter((s) => s !== "Blocked")
              .map((status) => (
                <div
                  key={status}
                  className={`min-w-[240px] flex-shrink-0 rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-all sm:min-w-[280px] sm:p-4 md:min-w-0 ${
                    dragOverStatus === status
                      ? "ring-2 ring-blue-400 border-blue-400 shadow-lg"
                      : ""
                  }`}
                  onDragOver={(e) => handleDragOver(e, status)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, status)}
                >
                  {/* Column Header */}
                  <div className="mb-3 flex items-center justify-between border-b border-gray-100 pb-2 sm:mb-4 sm:pb-3">
                    <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                      {status}
                    </h3>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        status === "Complete"
                          ? "bg-green-100 text-green-800"
                          : status === "In Progress"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {tasksByStatus[status].length}
                    </span>
                  </div>

                  {/* Tasks */}
                  <div className="min-h-[200px] space-y-2 p-1 sm:space-y-3">
                    {tasksByStatus[status].map((task) => (
                      <div
                        key={task.id}
                        draggable={canEdit}
                        onDragStart={(e) => handleDragStart(e, task.id)}
                        onClick={() => handleViewTask(task)}
                        className={`group rounded-lg border border-gray-200 bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-lg sm:p-3.5 ${
                          canEdit ? "cursor-pointer" : "cursor-pointer"
                        } ${draggedTaskId === task.id ? "opacity-50" : ""}`}
                      >
                        {/* Header with title and actions */}
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2 text-[11px] text-gray-500 sm:text-xs">
                              {task.formatted_reference && (
                                <span className="font-mono">
                                  {task.formatted_reference}
                                </span>
                              )}
                              {canEdit ? (
                                <select
                                  value={task.status}
                                  onChange={(e) =>
                                    onTaskUpdate(task.id, {
                                      status: e.target.value as
                                        | "not_started"
                                        | "in_progress"
                                        | "complete"
                                        | "blocked",
                                    })
                                  }
                                  className="rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600 transition hover:bg-white focus:border-transparent focus:bg-white focus:ring-2 focus:ring-blue-500"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="not_started">
                                    Not Started
                                  </option>
                                  <option value="in_progress">
                                    In Progress
                                  </option>
                                  <option value="complete">Complete</option>
                                  <option value="blocked">Blocked</option>
                                </select>
                              ) : (
                                <span className="rounded-md border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600">
                                  {formatTaskStatus(task.status)}
                                </span>
                              )}
                            </div>
                            <h4 className="text-sm font-semibold leading-tight text-gray-900">
                              {task.title}
                            </h4>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {canEdit && (
                              <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                <button
                                  onClick={(e) => handleEditTask(task, e)}
                                  className="rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                  title="Edit"
                                >
                                  <PencilIcon className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onTaskDelete(task.id);
                                  }}
                                  className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                  title="Delete"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                            {task.priority && (
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium sm:text-xs ${
                                  task.priority === "high"
                                    ? "bg-red-100 text-red-800"
                                    : task.priority === "medium"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {task.priority.charAt(0).toUpperCase() +
                                  task.priority.slice(1)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-600 sm:text-xs">
                          {task.area_name && (
                            <span className="inline-flex items-center rounded-full bg-gray-500 px-2 py-0.5 text-[11px] font-medium text-white sm:text-xs">
                              {task.area_name}
                            </span>
                          )}
                          {getContextDisplay(task) && (
                            <div className="flex items-center gap-1">
                              <Squares2X2Icon className="h-3 w-3 text-gray-400" />
                              <span className="truncate">
                                {getContextDisplay(task)}
                              </span>
                            </div>
                          )}
                          {task.okr_id && (
                            <div className="flex items-center gap-1">
                              <RocketLaunchIcon className="h-3 w-3 text-gray-400" />
                              <span className="truncate">
                                {task.okr_objective || "Unknown OKR"}
                              </span>
                            </div>
                          )}
                        </div>

                        {task.description && (
                          <p className="mb-2 line-clamp-2 text-[11px] leading-snug text-gray-500 sm:text-xs">
                            {task.description}
                          </p>
                        )}

                        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 sm:text-xs">
                            {task.assigned_to_id ? (
                              <>
                                <UserIcon className="h-3 w-3" />
                                <span className="max-w-[120px] truncate">
                                  {getAssigneeName(task)}
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-400">Unassigned</span>
                            )}
                          </div>
                          {task.due_date && (
                            <div className="flex items-center gap-1 text-[11px] text-gray-500 sm:text-xs">
                              <CalendarIcon className="h-3 w-3" />
                              <span>
                                {new Date(task.due_date).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Fixed Blocked Tasks Section at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
              Blocked Tasks
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              {tasksByStatus["Blocked"].length}
            </span>
            <button
              onClick={() => setShowBlockedPanel(!showBlockedPanel)}
              className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 sm:text-sm"
            >
              {showBlockedPanel ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        {showBlockedPanel && (
          <div
            className={`mt-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-3 transition-all ${
              dragOverStatus === "Blocked" ? "border-red-300 bg-red-50" : ""
            }`}
            onDragOver={(e) => handleDragOver(e, "Blocked")}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, "Blocked")}
          >
            {tasksByStatus["Blocked"].length === 0 ? (
              <div className="flex h-[140px] items-center justify-center">
                <p className="text-sm text-gray-500">No blocked tasks</p>
              </div>
            ) : (
              <div className="h-[160px] overflow-y-auto">
                <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 xl:grid-cols-3">
                  {tasksByStatus["Blocked"].map((task) => (
                    <div
                      key={task.id}
                      draggable={canEdit}
                      onDragStart={(e) => handleDragStart(e, task.id)}
                      onClick={() => handleViewTask(task)}
                      className={`group cursor-pointer rounded-lg border-2 border-red-200 bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-red-400 hover:shadow-lg sm:p-3.5 ${
                        draggedTaskId === task.id ? "opacity-50" : ""
                      }`}
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="mb-1 flex items-center gap-2 text-[11px] text-red-600 sm:text-xs">
                            {task.formatted_reference && (
                              <span className="font-mono">
                                {task.formatted_reference}
                              </span>
                            )}
                            {canEdit ? (
                              <select
                                value={task.status}
                                onChange={(e) =>
                                  onTaskUpdate(task.id, {
                                    status: e.target.value as
                                      | "not_started"
                                      | "in_progress"
                                      | "complete"
                                      | "blocked",
                                  })
                                }
                                className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700 transition hover:bg-white focus:border-transparent focus:bg-white focus:ring-2 focus:ring-red-300"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <option value="not_started">Not Started</option>
                                <option value="in_progress">In Progress</option>
                                <option value="complete">Complete</option>
                                <option value="blocked">Blocked</option>
                              </select>
                            ) : (
                              <span className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] text-red-700">
                                {formatTaskStatus(task.status)}
                              </span>
                            )}
                          </div>
                          <h4 className="text-sm font-semibold leading-tight text-red-900">
                            {task.title}
                          </h4>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {canEdit && (
                            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                onClick={(e) => handleEditTask(task, e)}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                title="Edit"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTaskDelete(task.id);
                                }}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                title="Delete"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                          {task.priority && (
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium sm:text-xs ${
                                task.priority === "high"
                                  ? "bg-red-100 text-red-800"
                                  : task.priority === "medium"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {task.priority.charAt(0).toUpperCase() +
                                task.priority.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-600 sm:text-xs">
                        {task.area_name && (
                          <span className="inline-flex items-center rounded-full bg-gray-500 px-2 py-0.5 text-[11px] font-medium text-white sm:text-xs">
                            {task.area_name}
                          </span>
                        )}
                        {getContextDisplay(task) && (
                          <div className="flex items-center gap-1">
                            <Squares2X2Icon className="h-3 w-3 text-gray-400" />
                            <span className="truncate">
                              {getContextDisplay(task)}
                            </span>
                          </div>
                        )}
                        {task.okr_id && (
                          <div className="flex items-center gap-1">
                            <RocketLaunchIcon className="h-3 w-3 text-gray-400" />
                            <span className="truncate">
                              {task.okr_objective || "Unknown OKR"}
                            </span>
                          </div>
                        )}
                      </div>

                      {task.description && (
                        <p className="mb-2 line-clamp-2 text-[11px] leading-snug text-gray-500 sm:text-xs">
                          {task.description}
                        </p>
                      )}

                      <div className="flex items-center justify-between border-t border-red-100 pt-2">
                        <div className="flex items-center gap-1 text-[11px] text-gray-500 sm:text-xs">
                          {task.assigned_to_id ? (
                            <>
                              <UserIcon className="h-3 w-3" />
                              <span className="max-w-[120px] truncate">
                                {getAssigneeName(task)}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </div>
                        {task.due_date && (
                          <div className="flex items-center gap-1 text-[11px] text-gray-500 sm:text-xs">
                            <CalendarIcon className="h-3 w-3" />
                            <span>
                              {new Date(task.due_date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                },
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Task Modal - for creating new tasks */}
      {showAddTask && (
        <TaskModal
          task={null} // null = create mode
          organizationId={organizationId}
          onClose={() => {
            setShowAddTask(false);
          }}
          onSave={async (createdTask) => {
            // TaskModal handles the create API call and returns the new task
            // Notify parent to refresh the task list
            await onTaskCreate(createdTask);
            setShowAddTask(false);
          }}
          segments={segments}
          propositions={propositions}
          groups={groups}
          positions={positions}
          companies={companies}
          isAdmin={canEdit}
          initialValues={
            entityType === "okr" && entityId
              ? { okr_id: entityId } // Only pre-fill OKR alignment for OKR pages
              : entityType && entityId
                ? {
                    context_type: entityType,
                    context_object_id: entityId,
                  }
                : undefined
          }
        />
      )}

      {/* Task Detail Panel - for viewing and editing existing tasks */}
      <TaskDetailPanel
        task={selectedTask}
        isOpen={showDetailPanel}
        onClose={() => {
          setShowDetailPanel(false);
          setSelectedTask(null);
        }}
        onUpdate={(updatedTask) => {
          // Update the task in the parent component
          onTaskUpdate(updatedTask.id!, updatedTask);
          setSelectedTask(updatedTask);
        }}
        onDelete={async (taskId) => {
          await onTaskDelete(taskId);
          setShowDetailPanel(false);
          setSelectedTask(null);
        }}
        organizationId={organizationId}
        currentUserId="" // Will be fetched from AuthContext by the panel
        canEdit={canEdit}
        isAdmin={canEdit}
        segments={segments}
        propositions={propositions}
        groups={groups}
        positions={positions}
        companies={companies}
      />
    </div>
  );
};

export default TaskKanbanView;
